import { useEffect, useState } from "react";
import { useI18n } from "i18n";
import { useAppDispatch } from "store/hooks";
import useCompany from "modules/company/hooks/useCompany";
import useNotificationActions from "modules/notification/hooks/useNotificationActions";
import useCompanyWhiteLabel from "modules/company/hooks/useCompanyWhiteLabel";
import { setAppLogo } from "modules/appSetting";
import { Box } from "@remo-co/ui-core/src/components/Box";
import { Typography } from "@remo-co/ui-core/src/components/Typography";
import { SwitchInput } from "@remo-co/ui-core/src/components/SwitchInput";
import { styles } from "./styles";

interface Props {
  enableWhitelabelling: boolean;
}

export const CustomBranding = ({ enableWhitelabelling }: Props) => {
  const dispatch = useAppDispatch();
  const { t } = useI18n(["common", "settings"]);
  const { company, saveCompany } = useCompany();
  const { enableWhiteLabel, disableWhiteLabel } = useCompanyWhiteLabel();
  const { addErrorNotification, addSuccessNotification } =
    useNotificationActions();
  const [isEnabledCustomBranding, setIsEnabledCustomBranding] =
    useState<boolean>(false);
  const toggleIsEnabledCustomBranding = () => {
    if (isEnabledCustomBranding) {
      dispatch(setAppLogo(null));
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      disable();
    } else {
      dispatch(setAppLogo(company?.logoURL || null));
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      enable();
    }
    setIsEnabledCustomBranding(!isEnabledCustomBranding);
  };

  const enable = () => {
    enableWhiteLabel()
      // eslint-disable-next-line promise/prefer-await-to-then
      .then((newCompany) => {
        // eslint-disable-next-line promise/always-return
        if (newCompany === false) {
          throw new Error();
        }

        // eslint-disable-next-line promise/catch-or-return, promise/prefer-await-to-then
        saveCompany(newCompany).then(() => {
          // eslint-disable-next-line promise/always-return
          setIsEnabledCustomBranding(newCompany.enabledWhiteLabel ?? false);
          addSuccessNotification({
            message: t("settings:enable.custom.branding"),
            position: "tc",
            autoDismiss: 0,
          });
        });
      })
      // eslint-disable-next-line promise/prefer-await-to-then
      .catch(() => {
        addErrorNotification({
          message: t("settings:custom.branding.fail"),
          position: "tc",
          autoDismiss: 0,
        });
      });
  };
  const disable = () => {
    disableWhiteLabel()
      // eslint-disable-next-line promise/prefer-await-to-then
      .then((newCompany) => {
        // eslint-disable-next-line promise/always-return
        if (newCompany === false) {
          throw new Error();
        }

        // eslint-disable-next-line promise/catch-or-return, promise/prefer-await-to-then
        saveCompany(newCompany).then(() => {
          // eslint-disable-next-line promise/always-return
          setIsEnabledCustomBranding(newCompany.enabledWhiteLabel ?? false);
          addSuccessNotification({
            message: t("settings:disable.custom.branding"),
            position: "tc",
            autoDismiss: 0,
          });
        });
      })
      // eslint-disable-next-line promise/prefer-await-to-then
      .catch(() => {
        addErrorNotification({
          message: t("settings:fail.disable.custom.branding"),
          position: "tc",
          autoDismiss: 0,
        });
      });
  };

  useEffect(() => {
    setIsEnabledCustomBranding(Boolean(company?.enabledWhiteLabel));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box sx={styles.container}>
      <Typography variant="h3">
        {t("settings:top.bar.event.branding")}
      </Typography>
      <Typography sx={styles.helpText} style={{ marginBottom: "10px" }}>
        {t("settings:replace.remo.logo")}
      </Typography>
      <SwitchInput
        label={t("settings:enable.top.bar.event.branding")}
        disabled={!enableWhitelabelling}
        checked={enableWhitelabelling ? isEnabledCustomBranding : false}
        onChange={toggleIsEnabledCustomBranding}
      />
    </Box>
  );
};
